import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap } from 'rxjs/operators';

import { env } from '@amfam/environment';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';
import { CookiesService } from '@amfam/shared/utility/shared-services/lib/cookies/cookies.service';

import { AGENT_INFO_BFF_FLAG } from '../constants/agent-info.flag';
import { Agent, initialAgent, Producers } from '../models/agent.model';
import {
  AgentActionTypes,
  LoadAgentAction,
  LoadAgentsFailAction,
  LoadAgentsSuccessAction
} from './agent.actions';
import { selectAgents } from './agent.selectors';

function getOptions() {
  const headers = new HttpHeaders({ authId: 'cdhtp1', userId: 'cdhtp1' });
  return { headers: headers };
}

export const loadAgent = createEffect(
  (actions$ = inject(Actions), http = inject(HttpClient)) => {
    return actions$.pipe(
      ofType(AgentActionTypes.LoadAgents),
      map((action: ReturnType<typeof LoadAgentAction>) => action.payload),
      mergeMap(payload => {
        const options = getOptions();
        const url = `${env.userAccountApi}/producers/${payload?.id}`;
        return http.get<Producers>(url, options).pipe(
          map(
            (data): Agent => ({
              ...initialAgent,
              id: data.producer.producerId,
              agentId: data.producer.producerId,
              globalNickname: data.producer.alternateId.globalNickname,
              fullName: data.producer.fullName,
              firstName: data.producer.firstName,
              middleName: data.producer.middleName,
              lastName: data.producer.lastName,
              nickName: data.producer.nickName,
              offices: data.producer.offices,
              websiteUrl: data.producer.producerDetail.webSiteUrl,
              emailFormUrl: data?.producer?.producerDetail?.emailFormUrl,
              photoUrl: data.producer.producerDetail.photoOfAgentUrl,
              isAmericanStarCertified: data.producer.producerDetail.americanStarIndicator,
              // Underlying data stucture for getting the emails is not reliable at this time
              emails: data.producer.contactDetail.emails,
              agencyEmails: data.producer.contactDetail.agencyEmails,
              phones: data.producer.contactDetail.phones
            })
          ),
          map((agent: Agent) => LoadAgentsSuccessAction({ payload: agent })),
          catchError(err => of(LoadAgentsFailAction(err)))
        );
      })
    );
  },
  { functional: true }
);

export const updateAgentCookie = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    cookies = inject(CookiesService),
    featureFlags = inject(FeatureFlagService)
  ) => {
    return actions$.pipe(
      ofType(AgentActionTypes.LoadAgentsSuccess),
      // With the flag on, GET /agent-info sets and removes the cookie (see the agent cookie guard).
      filter(() => !featureFlags.isEnabled(AGENT_INFO_BFF_FLAG)),
      map((action: ReturnType<typeof LoadAgentsSuccessAction>) => action.payload),
      concatLatestFrom(() => store.select(selectAgents)),
      map(([payload, agents]) => {
        agents = agents === undefined ? [] : agents;
        // get unique nicknames from new payload plus what is already in store
        return [
          ...Array.from(
            new Set([
              // global nickname from new payload
              payload?.globalNickname,
              // global nicknames from store
              ...agents
                // get global nickname for each agent in store
                .map(agent => agent.globalNickname)
            ])
          )
        ].filter(globalNickname => globalNickname !== undefined) as string[];
      }),
      tap((globalNicknames: string[]) => {
        const expirationDate = new Date();
        expirationDate.setUTCFullYear(expirationDate.getUTCFullYear() + 2);

        // remove agentID cookie if there are no agents, or update if there are agents
        if (globalNicknames.length === 0) {
          cookies.removeItem('agentID', '/', '.amfam.com');
        } else {
          cookies.setItem('agentID', globalNicknames.join('~'), expirationDate, '/', '.amfam.com');
        }
      })
    );
  },
  { functional: true, dispatch: false }
);
